"use client";

import { motion } from "framer-motion";

export interface AnimatedIconProps {
	isHovered: boolean;
}

const svgProps = {
	xmlns: "http://www.w3.org/2000/svg",
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: 2,
	strokeLinecap: "round" as const,
	strokeLinejoin: "round" as const,
};

// LayoutDashboard — staggered rect bounce
export function AnimatedLayoutDashboard({ isHovered }: AnimatedIconProps) {
	const rects = [
		{ width: 7, height: 9, x: 3, y: 3, rx: 1 },
		{ width: 7, height: 5, x: 14, y: 3, rx: 1 },
		{ width: 7, height: 9, x: 14, y: 12, rx: 1 },
		{ width: 7, height: 5, x: 3, y: 16, rx: 1 },
	];

	return (
		<svg {...svgProps}>
			{rects.map((rect, i) => (
				<motion.rect
					key={i}
					{...rect}
					initial={false}
					animate={
						isHovered ? { translateY: [0, -1.5, 0], opacity: [1, 0.6, 1] } : {}
					}
					transition={{ duration: 0.3, delay: i * 0.06, ease: "easeInOut" }}
				/>
			))}
		</svg>
	);
}

// Activity — heartbeat line draws itself
export function AnimatedActivity({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.path
				d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"
				initial={false}
				animate={isHovered ? { pathLength: [0, 1] } : { pathLength: 1 }}
				transition={{ duration: 0.5, ease: "easeInOut" }}
			/>
		</svg>
	);
}

// ChartColumnBig — bars grow from bottom
export function AnimatedChartColumnBig({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<path d="M3 3v16a2 2 0 0 0 2 2h16" />
			<motion.rect
				x={7}
				y={8}
				width={4}
				height={9}
				rx={1}
				initial={false}
				animate={isHovered ? { scaleY: [0, 1.1, 1] } : { scaleY: 1 }}
				transition={{ duration: 0.4, delay: 0.05, ease: "easeOut" }}
				style={{ transformOrigin: "bottom", transformBox: "fill-box" }}
			/>
			<motion.rect
				x={15}
				y={5}
				width={4}
				height={12}
				rx={1}
				initial={false}
				animate={isHovered ? { scaleY: [0, 1.1, 1] } : { scaleY: 1 }}
				transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
				style={{ transformOrigin: "bottom", transformBox: "fill-box" }}
			/>
		</svg>
	);
}

// BarChart3 (ChartColumn) — vertical bar lines draw upward
export function AnimatedBarChart3({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<path d="M3 3v16a2 2 0 0 0 2 2h16" />
			<motion.path
				d="M8 17v-3"
				initial={false}
				animate={isHovered ? { pathLength: [0, 1] } : { pathLength: 1 }}
				transition={{ duration: 0.3, delay: 0, ease: "easeOut" }}
			/>
			<motion.path
				d="M13 17V5"
				initial={false}
				animate={isHovered ? { pathLength: [0, 1] } : { pathLength: 1 }}
				transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
			/>
			<motion.path
				d="M18 17V9"
				initial={false}
				animate={isHovered ? { pathLength: [0, 1] } : { pathLength: 1 }}
				transition={{ duration: 0.3, delay: 0.2, ease: "easeOut" }}
			/>
		</svg>
	);
}

// ChartArea — trend line draws itself across the axes
export function AnimatedChartArea({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<path d="M3 3v16a2 2 0 0 0 2 2h16" />
			<motion.path
				d="M7 15l3-4 3 2 4-6"
				initial={false}
				animate={isHovered ? { pathLength: [0, 1] } : { pathLength: 1 }}
				transition={{ duration: 0.5, ease: "easeInOut" }}
			/>
		</svg>
	);
}

// Key — rotates like turning a lock
export function AnimatedKey({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.g
				initial={false}
				animate={isHovered ? { rotate: [0, -15, 8, 0] } : { rotate: 0 }}
				transition={{ duration: 0.5, ease: "easeInOut" }}
				style={{ transformOrigin: "7.5px 15.5px" }}
			>
				<path d="m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4" />
				<path d="m21 2-9.6 9.6" />
				<circle cx="7.5" cy="15.5" r="5.5" />
			</motion.g>
		</svg>
	);
}

// Settings — gear rotates, center circle stays
export function AnimatedSettings({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.path
				d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"
				initial={false}
				animate={isHovered ? { rotate: 180 } : { rotate: 0 }}
				transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
				style={{ transformOrigin: "12px 12px" }}
			/>
			<circle cx="12" cy="12" r="3" />
		</svg>
	);
}

// KeySquare — key body jiggles, square head pulses
export function AnimatedKeySquare({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.path
				d="M12.4 2.7c.9-.9 2.5-.9 3.4 0l5.5 5.5c.9.9.9 2.5 0 3.4l-3.7 3.7c-.9.9-2.5.9-3.4 0L8.7 9.8c-.9-.9-.9-2.5 0-3.4z"
				initial={false}
				animate={isHovered ? { scale: [1, 1.12, 1] } : { scale: 1 }}
				transition={{ duration: 0.4, ease: "easeInOut" }}
				style={{ transformOrigin: "15px 8px" }}
			/>
			<motion.g
				initial={false}
				animate={isHovered ? { rotate: [0, -6, 4, -2, 0] } : { rotate: 0 }}
				transition={{ duration: 0.5, ease: "easeInOut" }}
				style={{ transformOrigin: "12px 12px" }}
			>
				<path d="m14 7 3 3" />
				<path d="m9.4 10.6-6.814 6.814A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814" />
			</motion.g>
		</svg>
	);
}

// KeyRound — key body jiggles, dot pulses
export function AnimatedKeyRound({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.path
				d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"
				initial={false}
				animate={isHovered ? { rotate: [0, -8, 5, -3, 0] } : { rotate: 0 }}
				transition={{ duration: 0.5, ease: "easeInOut" }}
				style={{ transformOrigin: "12px 12px" }}
			/>
			<motion.circle
				cx="16.5"
				cy="7.5"
				r=".5"
				fill="currentColor"
				initial={false}
				animate={isHovered ? { scale: [1, 2, 1] } : { scale: 1 }}
				transition={{ duration: 0.3, delay: 0.1 }}
				style={{ transformOrigin: "16.5px 7.5px" }}
			/>
		</svg>
	);
}

// Shield — protective pulse
export function AnimatedShield({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.path
				d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
				initial={false}
				animate={isHovered ? { scale: [1, 1.12, 1] } : { scale: 1 }}
				transition={{ duration: 0.4, ease: "easeInOut" }}
				style={{ transformOrigin: "12px 12px" }}
			/>
		</svg>
	);
}

// ShieldAlert — shield steady, exclamation shakes
export function AnimatedShieldAlert({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
			<motion.path
				d="M12 8v4"
				initial={false}
				animate={
					isHovered ? { translateX: [0, -1, 1, -1, 1, 0] } : { translateX: 0 }
				}
				transition={{ duration: 0.4, ease: "easeInOut" }}
			/>
			<motion.path
				d="M12 16h.01"
				initial={false}
				animate={isHovered ? { scale: [1, 1.8, 1] } : { scale: 1 }}
				transition={{ duration: 0.3, delay: 0.1 }}
				style={{ transformOrigin: "12px 16px" }}
			/>
		</svg>
	);
}

// RadioTower — mast steady, waves pulse outward
export function AnimatedRadioTower({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<path d="m8 22 4-10 4 10" />
			<path d="M9.5 18h5" />
			<circle cx="12" cy="9" r="2" />
			{[
				"M7.8 4.7a6.14 6.14 0 0 0-.8 7.5",
				"M16.2 4.8c2 2 2.26 5.11.8 7.47",
			].map((d, i) => (
				<motion.path
					key={d}
					d={d}
					initial={false}
					animate={isHovered ? { opacity: [1, 0.2, 1] } : { opacity: 1 }}
					transition={{ duration: 0.5, delay: i * 0.1 }}
				/>
			))}
			{[
				"M4.9 19.1C1 15.2 1 8.8 4.9 4.9",
				"M19.1 4.9C23 8.8 23 15.1 19.1 19",
			].map((d, i) => (
				<motion.path
					key={d}
					d={d}
					initial={false}
					animate={isHovered ? { opacity: [1, 0.2, 1] } : { opacity: 1 }}
					transition={{ duration: 0.5, delay: (i + 1.5) / 10 }}
				/>
			))}
		</svg>
	);
}

// BadgeCheck — badge steady, check draws itself
export function AnimatedBadgeCheck({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
			<motion.path
				d="m9 12 2 2 4-4"
				initial={false}
				animate={isHovered ? { pathLength: [0, 1] } : { pathLength: 1 }}
				transition={{ duration: 0.4, ease: "easeInOut" }}
			/>
		</svg>
	);
}

// FileText — page steady, lines draw in
export function AnimatedFileText({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
			<path d="M14 2v4a2 2 0 0 0 2 2h4" />
			{["M10 9H8", "M16 13H8", "M16 17H8"].map((d, i) => (
				<motion.path
					key={d}
					d={d}
					initial={false}
					animate={isHovered ? { pathLength: [0, 1] } : { pathLength: 1 }}
					transition={{ duration: 0.3, delay: i * 0.08 }}
				/>
			))}
		</svg>
	);
}

// MessageSquare — bubble bounces
export function AnimatedMessageSquare({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.path
				d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"
				initial={false}
				animate={isHovered ? { translateY: [0, -2, 0] } : { translateY: 0 }}
				transition={{ duration: 0.3, ease: "easeInOut" }}
			/>
		</svg>
	);
}

// BotMessageSquare — eyes blink, antenna wiggles, ears extend
export function AnimatedBotMessageSquare({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.path
				d="M12 6V2H8"
				initial={false}
				animate={isHovered ? { rotate: [0, -10, 10, -5, 0] } : { rotate: 0 }}
				transition={{ duration: 0.5, ease: "easeInOut" }}
				style={{ transformOrigin: "12px 6px" }}
			/>
			<motion.path
				d="M15 11v2"
				initial={false}
				animate={isHovered ? { scaleY: [1, 0.1, 1] } : { scaleY: 1 }}
				transition={{ duration: 0.3, delay: 0.15 }}
				style={{ transformOrigin: "center", transformBox: "fill-box" }}
			/>
			<motion.path
				d="M2 12h2"
				initial={false}
				animate={isHovered ? { translateX: [0, -1.5, 0] } : { translateX: 0 }}
				transition={{ duration: 0.3, ease: "easeInOut" }}
			/>
			<motion.path
				d="M20 12h2"
				initial={false}
				animate={isHovered ? { translateX: [0, 1.5, 0] } : { translateX: 0 }}
				transition={{ duration: 0.3, ease: "easeInOut" }}
			/>
			<path d="M20 16a2 2 0 0 1-2 2H8.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 4 20.286V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
			<motion.path
				d="M9 11v2"
				initial={false}
				animate={isHovered ? { scaleY: [1, 0.1, 1] } : { scaleY: 1 }}
				transition={{ duration: 0.3, delay: 0.15 }}
				style={{ transformOrigin: "center", transformBox: "fill-box" }}
			/>
		</svg>
	);
}

// Percent — bounces on hover
export function AnimatedPercent({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.line
				x1="19"
				y1="5"
				x2="5"
				y2="19"
				initial={false}
				animate={isHovered ? { rotate: [0, -10, 5, 0] } : { rotate: 0 }}
				transition={{ duration: 0.4, ease: "easeInOut" }}
				style={{ transformOrigin: "12px 12px" }}
			/>
			<motion.circle
				cx="6.5"
				cy="6.5"
				r="2.5"
				initial={false}
				animate={isHovered ? { scale: [1, 1.3, 1] } : { scale: 1 }}
				transition={{ duration: 0.3, delay: 0.05 }}
				style={{ transformOrigin: "6.5px 6.5px" }}
			/>
			<motion.circle
				cx="17.5"
				cy="17.5"
				r="2.5"
				initial={false}
				animate={isHovered ? { scale: [1, 1.3, 1] } : { scale: 1 }}
				transition={{ duration: 0.3, delay: 0.15 }}
				style={{ transformOrigin: "17.5px 17.5px" }}
			/>
		</svg>
	);
}

// Terminal — chevron prompt slides forward, cursor blinks
export function AnimatedTerminal({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.polyline
				points="4 17 10 11 4 5"
				initial={false}
				animate={isHovered ? { translateX: [0, 2, 0] } : { translateX: 0 }}
				transition={{ duration: 0.35, ease: "easeInOut" }}
			/>
			<motion.line
				x1="12"
				y1="19"
				x2="20"
				y2="19"
				initial={false}
				animate={isHovered ? { opacity: [1, 0.2, 1] } : { opacity: 1 }}
				transition={{ duration: 0.5, delay: 0.1, ease: "easeInOut" }}
			/>
		</svg>
	);
}

// Users — second person pops in, both bob on hover
export function AnimatedUsers({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.g
				initial={false}
				animate={isHovered ? { translateY: [0, -1.5, 0] } : { translateY: 0 }}
				transition={{ duration: 0.3, ease: "easeInOut" }}
			>
				<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
				<circle cx="9" cy="7" r="4" />
			</motion.g>
			<motion.g
				initial={false}
				animate={
					isHovered ? { scale: [1, 1.15, 1], opacity: [0.6, 1] } : { scale: 1 }
				}
				transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
				style={{ transformOrigin: "20px 12px" }}
			>
				<path d="M22 21v-2a4 4 0 0 0-3-3.87" />
				<path d="M16 3.13a4 4 0 0 1 0 7.75" />
			</motion.g>
		</svg>
	);
}

// ExternalLink — arrow shoots out diagonally
export function AnimatedExternalLink({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.path
				d="M15 3h6v6"
				initial={false}
				animate={
					isHovered
						? { translateX: [0, 2, 0], translateY: [0, -2, 0] }
						: { translateX: 0, translateY: 0 }
				}
				transition={{ duration: 0.3, ease: "easeInOut" }}
			/>
			<motion.path
				d="M10 14 21 3"
				initial={false}
				animate={
					isHovered
						? { translateX: [0, 2, 0], translateY: [0, -2, 0] }
						: { translateX: 0, translateY: 0 }
				}
				transition={{ duration: 0.3, ease: "easeInOut" }}
			/>
			<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
		</svg>
	);
}

// Building2 (SSO) — subtle lift on hover
export function AnimatedBuilding2({ isHovered }: AnimatedIconProps) {
	return (
		<svg {...svgProps}>
			<motion.g
				initial={false}
				animate={isHovered ? { translateY: [0, -1.5, 0] } : { translateY: 0 }}
				transition={{ duration: 0.3, ease: "easeInOut" }}
			>
				<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
				<path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
				<path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
				<path d="M10 6h4" />
				<path d="M10 10h4" />
				<path d="M10 14h4" />
				<path d="M10 18h4" />
			</motion.g>
		</svg>
	);
}
